/**
 * The Report Studio — `/um/umpires/:tmsPersonId/report`.
 *
 * Authority: `um-app/06-report-studio.md`. *"The same login in any browser: the phone
 * captures, the laptop writes."* — so this is the same route on either device, and the layout
 * is a single column that widens rather than a desktop-only screen.
 *
 * **What is on it, and what is honestly missing.** Both panes are now here. The right pane of
 * §4 — the TD's closing-remarks block, the editable narrative, the 800-character meter, the
 * pre-push strip and W3 — is `UmReportComposer`; the LEFT pane of §3 — `Evidence — All
 * Tournament`, the criteria groups, the count line and the Report Readiness ring — is
 * `UmEvidencePane`, drawn from the released-notes-per-umpire projection that now exists.
 *
 * **The two panes are fetched independently and neither waits on the other**, because they
 * fail differently and both failures must be survivable. §6: *"The evidence is always readable
 * and the report is always writable by hand — an AI failure must never block a report
 * deadline."* The same holds in reverse: an evidence read that fails leaves the composer
 * working, with a banner saying the pane could not be loaded rather than an empty pane
 * implying the tournament recorded nothing.
 *
 * Still absent rather than stubbed: the AI draft itself (`Re-Draft From Notes`), and with it
 * the provenance strip `Drafted From Your Notes + Team Observations` — there is no drafting
 * call in this slice, so nothing may say there was one.
 */

import { Link, useParams } from "react-router-dom";

import {
  fetchMyUmpires,
  fetchUmEvidence,
  fetchUmMarkingPanel,
  fetchUmReport,
} from "../../api/client.js";
import { HowToLink, HowToUse } from "../../app/HowToUse.js";
import { FailBanner, Skeleton } from "../../app/states.js";
import { useAsync } from "../../app/useAsync.js";
import { UM_ROOT_PATH } from "../../app/UmShell.js";
import { UmEvidencePane } from "../../features/um/UmEvidencePane.js";
import { UmReportComposer } from "../../features/um/UmReportComposer.js";

export function UmReportPage(): React.JSX.Element {
  const { tmsPersonId } = useParams<{ tmsPersonId: string }>();
  const id = tmsPersonId ?? "";

  const report = useAsync(() => fetchUmReport(id), [id]);
  const mine = useAsync(() => fetchMyUmpires(), []);
  /**
   * Where the marks stand, for §4.5's advisory line. Fetched separately and never awaited by
   * the composer: the strip is advice, and *"none of them blocks the push"* — a marking read
   * that failed must not stop a report being written.
   */
  const panel = useAsync(() => fetchUmMarkingPanel(id), [id]);
  /**
   * §3's evidence pool. Fetched independently of the report and never awaited by it: the
   * composer must work when this read fails, and the pane must render when the report read
   * fails. Neither is allowed to be the other's precondition.
   */
  const evidence = useAsync(() => fetchUmEvidence(id), [id]);

  const row =
    mine.status === "ready"
      ? mine.data.umpires.find((umpire) => umpire.tmsPersonId === id)
      : undefined;
  const name = row?.mediaName ?? `TMS person ${id}`;

  return (
    <>
      <header className="umsect">
        {/* Back arrows name their destination, never a bare `‹` (gap J7). */}
        <Link className="umback" to={`${UM_ROOT_PATH}/umpires`}>
          ‹ Your Umpires
        </Link>
        <h2>Report Studio</h2>
        <span className="umsect__kicker">{name} · Owner UM: You</span>
        <HowToUse id="um-report" title="How to use Report Studio">
          <li>
            <HowToLink to="evidence-pane">Evidence — All Tournament</HowToLink> is every
            released note about this umpire, grouped by criterion. Write the report from it.
          </li>
          <li>
            <HowToLink to="readiness-ring">Report Readiness</HowToLink> counts how many criteria
            have released observations behind them. It measures evidence, never quality, and it
            blocks nothing.
          </li>
          <li>
            Write the narrative in{" "}
            <HowToLink to="um-composer">Your Report — Whole Tournament</HowToLink>. It is yours to
            edit.
          </li>
          <li>
            <HowToLink to="um-preview">The preview below it</HowToLink> shows the finished
            report exactly as TMS will receive it, with the Technical Delegate&rsquo;s closing
            remarks joined on.
          </li>
          <li>
            One report covers the whole tournament — both competitions, never two.
          </li>
          <li>You are the one who sends it. Nothing here goes on its own.</li>
        </HowToUse>
      </header>

      <p className="umnote">
        One report for the whole tournament — both competitions, never two. Your notes name
        who wrote them inside the team; the report itself <b>never does</b>.
      </p>

      {/* The left pane first, as the spec draws it: the evidence is what the report is
          written from, and it stays readable whatever the composer is doing. */}
      {evidence.status === "loading" ? <Skeleton rows={5} /> : null}
      {evidence.status === "error" ? <FailBanner error={evidence.error} /> : null}
      {evidence.status === "ready" ? <UmEvidencePane evidence={evidence.data} /> : null}

      {report.status === "loading" ? <Skeleton rows={6} /> : null}
      {report.status === "error" ? <FailBanner error={report.error} /> : null}
      {report.status === "ready" ? (
        <UmReportComposer
          key={id}
          umpireName={name}
          initialReport={report.data}
          {...(panel.status === "ready" ? { panel: panel.data } : {})}
          {...(panel.status === "ready"
            ? {
                marks: {
                  stated: panel.data.criteria.length - panel.data.unmarkedCriteria.length,
                  total: panel.data.criteria.length,
                  outcome: panel.data.outcome,
                  pushedAt: panel.data.lastPushedAt,
                },
              }
            : {})}
        />
      ) : null}

      <p className="umfoot">
        <Link to={`${UM_ROOT_PATH}/umpires/${id}/marking`}>Set the marks</Link> — the agreed
        category marks are their own press, on the marking screen.
      </p>
    </>
  );
}
